import { css } from '@emotion/react'
import { HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { resolveName, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { getIn } from 'final-form'
import { useForm } from 'react-final-form'
import { MetaPath, MetaRoot } from 'util/metaPath'

import { PassouFalhouEnum } from '../enums-consultaSeguimento'

export type ExemplosVerticalFieldModel<T extends string> = Record<PassouFalhouEnum, Record<T, SimNaoEnum | undefined>>

export type ExemplosVerticalFieldOptions<T extends string> = Record<
  PassouFalhouEnum,
  Array<{ value: T; label: string }>
>

interface ExemplosVerticalFieldProps<T extends string> {
  name: MetaPath<ExemplosVerticalFieldModel<T>>
  label: string
  options: ExemplosVerticalFieldOptions<T>
  title?: string
  titlePassou?: string
  titleFalhou?: string
  required?: boolean
  disabled?: boolean
  onChange?: (value: Record<T, SimNaoEnum>) => void
}

export function ExemplosVerticalField<T extends string>(props: Readonly<ExemplosVerticalFieldProps<T>>) {
  const { name, label, options, title, titlePassou, titleFalhou, required, disabled, onChange } = props

  const theme = useTheme()
  const { getState } = useForm()
  const styles = createStyles(theme)

  const namePassou = name.PASSOU as MetaRoot<Record<T, SimNaoEnum | undefined>>
  const nameFalhou = name.FALHOU as MetaRoot<Record<T, SimNaoEnum | undefined>>

  const handleChange = (group: PassouFalhouEnum, option: T, value: SimNaoEnum) => {
    if (onChange) {
      const fieldValue = getIn(getState().values, resolveName(name))
      onChange({
        ...fieldValue,
        [group]: {
          ...fieldValue[group],
          [option]: value,
        },
      })
    }
  }

  return (
    <VFlow vSpacing={0.5}>
      {required ? (
        <RequiredLabel color={disabled ? 'disabled' : 'normal'} fontWeight='bold' label={label} />
      ) : (
        <Text color={disabled ? 'disabled' : 'normal'} fontWeight='bold'>
          {label}
        </Text>
      )}
      <VFlow vSpacing={0}>
        {title && (
          <HFlow style={styles.section}>
            <div />
            <div css={styles.title}>
              <Text color='inherit'>“{title}”</Text>
            </div>
          </HFlow>
        )}
        <Box style={styles.box}>
          <HFlow style={styles.section}>
            <Text style={styles.sectionLabel}>Exemplos Passou</Text>
            <VFlow vSpacing={0} style={styles.sectionList}>
              {titlePassou && (
                <div css={styles.sectionListHeader}>
                  <Text color='inherit'>“{titlePassou}”</Text>
                </div>
              )}
              {options.PASSOU.map((item) => (
                <HFlow
                  key={item.value}
                  alignItems='center'
                  justifyContent='space-between'
                  style={styles.sectionListItem}
                >
                  <Text>{item.label}</Text>
                  <SimNaoRadioGroupField
                    name={namePassou[item.value]}
                    onChange={(value) =>
                      handleChange(PassouFalhouEnum.PASSOU, item.value, value.currentTarget.value as SimNaoEnum)
                    }
                  />
                </HFlow>
              ))}
            </VFlow>
          </HFlow>
          <HFlow style={styles.section}>
            <Text style={styles.sectionLabel}>Exemplos Falhou</Text>
            <VFlow vSpacing={0} style={styles.sectionList}>
              {titleFalhou && (
                <div css={styles.sectionListHeader}>
                  <Text color='inherit'>“{titleFalhou}”</Text>
                </div>
              )}
              {options.FALHOU.map((item) => (
                <HFlow
                  key={item.value}
                  alignItems='center'
                  justifyContent='space-between'
                  style={styles.sectionListItem}
                >
                  <Text>{item.label}</Text>
                  <SimNaoRadioGroupField
                    name={nameFalhou[item.value]}
                    onChange={(value) =>
                      handleChange(PassouFalhouEnum.FALHOU, item.value, value.currentTarget.value as SimNaoEnum)
                    }
                  />
                </HFlow>
              ))}
            </VFlow>
          </HFlow>
        </Box>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
  `,
  section: css`
    grid-gap: 0;
    grid-template-columns: 5.75rem 1fr;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c60};
    }
  `,
  title: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    border-radius: 2px 2px 0 0;
    border: 1px solid ${theme.pallete.divider};
    border-bottom: 0;
    padding: 0.25rem 1rem;
  `,
  sectionLabel: css`
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  sectionList: css`
    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  sectionListHeader: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    padding: 0.25rem 1rem;
  `,
  sectionListItem: css`
    padding: 0.75rem 1rem;
  `,
})
